import { useMemo, useState, useEffect } from "react"

// ** Third Party Components
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  LabelList,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import {
  FileText,
  Receipt,
  Banknote,
  TrendingDown,
  Building2,
  PercentIcon,
  X,
  CheckCircle2,
  FolderOpen,
  AlertCircle,
  RefreshCw,
} from "lucide-react"

// ** UI Components
import { Button } from "@components/common/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/common/card"
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@components/common/chart"

// ** Helpers
import { formatCurrency } from "@pma/utils/utils"
import {
  classifyBillStatus,
  cn,
  getDrillDownSubtitle,
  getDrillDownTitle,
  utilizationColor,
  computeBillTotal
} from "@pma/utils/helpers"

// ** Types
import { CommonAnalyticsProps } from "@pma/types"

// ** STore
import { useAuth } from "@pma/stores/auth-store"


// ** Custom Hooks
import useTranslator from "@hooks/use-translator"
import { useDrillDown } from "@hooks/use-drill-down"
import { useFinancialQueries } from "@pma/hooks/use-financial-queries"
import { useFinancialComputed } from "@pma/hooks/use-financial-computed"


import { KpiCard as SharedKpiCard, ChartCard, DrillDownSheet, DrillDownTable } from "../widgets"
import { SummaryCard } from "./summary-card"
import { BoqBarLabel } from "./boq-bar-label"

// NOTE: Financial data is project-scoped, not user-scoped.
// The userIds filter from AnalyticsFilterBar is intentionally not applied here
// because BOQs and bills belong to projects/contractors, not individual users.

function FinancialKpiCard({
  label,
  value,
  icon,
  valueColor,
  gradient,
  iconBg,
  iconColor,
  subLabel,
}: {
  label: string
  value: string | number
  icon: React.ElementType
  valueColor: string
  gradient: string
  iconBg: string
  iconColor: string
  subLabel?: string
}) {
  return (
    <SharedKpiCard
      label={label}
      value={value}
      icon={icon}
      color='blue'
      gradient={gradient}
      iconBg={iconBg}
      iconColor={iconColor}
      valueColor={valueColor}
      subtitle={subLabel}
    />
  )
}

const FinancialAnalytics = ({ filters, onCountLoaded }: CommonAnalyticsProps) => {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"
  const { authConfig } = useAuth()
  const orgId = authConfig?.last_organization_id

  const [billStatusFilter, setBillStatusFilter] = useState<string | null>(null)
  const [selectedBillStatus, setSelectedBillStatus] = useState<string | null>(null)
  const { state: drillDown, open: openDrillDown, close: closeDrillDown } = useDrillDown()

  // Data fetching
  const { projects, allBoqs, allBills, isLoading, isError, refetchAll } = useFinancialQueries(
    orgId,
    filters,
  )

  // Computed values
  const computed = useFinancialComputed({
    projects,
    allBoqs,
    allBills,
    billStatusFilter,
    filters,
    translate,
  })

  const {
    projectNameMap,
    billStatusMap,
    totalBoqValue,
    totalBilled,
    remaining,
    boqCount,
    billCount,
    contractorCount,
    boqByProject,
    billStatusBreakdown,
    totalBillsUnfiltered,
    utilizationPct,
    allBillStatuses,
    boqNameMap,
    boqProjectConfig,
    billStatusConfig,
  } = computed

  const utilColor = utilizationColor(utilizationPct)

  // Report count to parent for tab badge
  useEffect(() => {
    if (onCountLoaded && boqCount > 0) onCountLoaded(boqCount)
  }, [boqCount, onCountLoaded])

  // Drill-down data
  const drillDownBills = useMemo(() => {
    if (!selectedBillStatus) return []
    return (allBills || []).filter((bill: any) => classifyBillStatus(bill) === selectedBillStatus)
  }, [allBills, selectedBillStatus])

  const drillDownBoqsByProject = useMemo(() => {
    if (drillDown?.type !== "boq") return []
    return (allBoqs || [])
      .filter((boq: any) => boq._projectId === drillDown.projectId)
      .map((boq: any) => {
        const total = Number(boq.boq_sum) || 0
        const boqBills = (allBills || []).filter((b: any) => b._boqId === boq.id)
        const billed = boqBills.reduce((s: number, b: any) => s + computeBillTotal(b, false), 0)
        return {
          name: boq.name || boq.title || `BOQ ${boq.id}`,
          total,
          billed,
          remaining: total - billed,
          utilization: total > 0 ? Math.round((billed / total) * 100) : 0,
        }
      })
  }, [drillDown, allBoqs, allBills])

  const drillDownAllBoqsUtilization = useMemo(() => {
    if (drillDown?.type !== "bill-status" || drillDown.statusKey !== "__utilization__") return []
    return (allBoqs || [])
      .map((boq: any) => {
        const total = Number(boq.boq_sum) || 0
        const boqBills = (allBills || []).filter((b: any) => b._boqId === boq.id)
        const billed = boqBills.reduce((s: number, b: any) => s + computeBillTotal(b, false), 0)
        return {
          name: boq.name || boq.title || `BOQ ${boq.id}`,
          project: projectNameMap[boq._projectId] || `Project ${boq._projectId}`,
          total,
          billed,
          remaining: total - billed,
          utilization: total > 0 ? Math.round((billed / total) * 100) : 0,
        }
      })
      .sort((a, b) => b.utilization - a.utilization)
  }, [drillDown, allBoqs, allBills, projectNameMap])

  // ---- Render ----

  if (isLoading)
    return (
      <div className='flex animate-pulse flex-col gap-6 py-4' dir={isHebrew ? "rtl" : "ltr"}>
        <div className='flex gap-2'>
          {[...Array(4)].map((_, i) => (
            <div key={i} className='h-6 w-16 rounded-full bg-gray-200' />
          ))}
        </div>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
          {[...Array(4)].map((_, i) => (
            <Card key={i} className='border-0 bg-gradient-to-br from-gray-50 to-white shadow-sm'>
              <CardContent className='flex flex-col items-center gap-2 p-5'>
                <div className='h-11 w-11 rounded-xl bg-gray-200' />
                <div className='h-8 w-28 rounded bg-gray-200' />
                <div className='h-3 w-20 rounded bg-gray-200' />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
          {[...Array(3)].map((_, i) => (
            <Card key={i} className='shadow-sm'>
              <CardContent className='flex items-center gap-3 p-4'>
                <div className='h-12 w-12 rounded-xl bg-gray-200' />
                <div className='flex-1 space-y-2'>
                  <div className='h-3 w-16 rounded bg-gray-200' />
                  <div className='h-7 w-12 rounded bg-gray-200' />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          {[...Array(2)].map((_, i) => (
            <Card key={i} className='h-[340px] shadow-sm'>
              <CardHeader className='pb-2'>
                <div className='h-4 w-40 rounded bg-gray-200' />
              </CardHeader>
              <CardContent className='flex h-[260px] items-center justify-center'>
                <div className='h-48 w-full rounded bg-gray-200' />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  if (isError) return
  ;<div
    className='flex flex-col items-center justify-center gap-4 py-24 text-center'
    dir={isHebrew ? "rtl" : "ltr"}
  >
    <div className='flex h-20 w-20 items-center justify-center rounded-2xl bg-red-50'>
      <AlertCircle className='h-10 w-10 text-red-400' />
    </div>
    <div>
      <h3 className='text-lg font-semibold text-gray-900'>{translate("Failed to Load Data")}</h3>
      <p className='mt-1 text-sm text-muted-foreground'>
        {translate("Could not load financial data")}
      </p>
    </div>
    <Button variant='outline' size='sm' onClick={refetchAll} className='gap-2'>
      <RefreshCw className='h-4 w-4' /> {translate("Retry")}
    </Button>
  </div>
  if (!(projects || []).length)
    return (
      <div
        className='flex flex-col items-center justify-center gap-4 py-24 text-center'
        dir={isHebrew ? "rtl" : "ltr"}
      >
        <div className='flex h-20 w-20 items-center justify-center rounded-2xl bg-blue-50'>
          <FolderOpen className='h-10 w-10 text-blue-400' />
        </div>
        <div>
          <h3 className='text-lg font-semibold text-gray-900'>
            {translate("No Financial Data Yet")}
          </h3>
          <p className='mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground'>
            {translate("Create BOQs in your projects to start tracking budgets")}
          </p>
        </div>
      </div>
    )

  return (
    <div className='flex flex-col gap-6 py-4' dir={isHebrew ? "rtl" : "ltr"}>
      {/* Bill Status Filter chips */}
      {allBillStatuses.length > 1 && (
        <div className='flex flex-wrap items-center gap-1.5'>
          <span className='text-xs font-semibold text-muted-foreground'>
            {translate("Filter by status")}:
          </span>
          <button
            onClick={() => setBillStatusFilter(null)}
            className={cn(
              "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold transition-colors",
              billStatusFilter === null
                ? "border-foreground bg-foreground text-background"
                : "border-border bg-transparent text-muted-foreground hover:border-foreground hover:text-foreground",
            )}
          >
            {translate("All")}
          </button>
          {allBillStatuses.map((status) => {
            const active = billStatusFilter === status
            const info = billStatusMap.get(status)!
            return (
              <button
                key={status}
                onClick={() => setBillStatusFilter(active ? null : status)}
                className={cn(
                  "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold transition-colors",
                  active
                    ? "border-transparent text-white"
                    : "border-border bg-transparent text-muted-foreground hover:border-foreground hover:text-foreground",
                )}
                style={active ? { background: info.color, borderColor: info.color } : undefined}
              >
                {active && (
                  <span className='mr-1 inline-block h-1.5 w-1.5 rounded-full bg-white/80' />
                )}
                {info.label}
              </button>
            )
          })}
        </div>
      )}

      {/* KPI Cards */}
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
        <FinancialKpiCard
          label={translate("Total BOQ Value")}
          value={formatCurrency(totalBoqValue, isHebrew)}
          icon={FileText}
          valueColor='text-blue-700'
          gradient='from-blue-50 to-white'
          iconBg='bg-blue-100'
          iconColor='text-blue-600'
        />
        <FinancialKpiCard
          label={translate("Total Billed")}
          value={formatCurrency(totalBilled, isHebrew)}
          icon={CheckCircle2}
          valueColor='text-emerald-700'
          gradient='from-emerald-50 to-white'
          iconBg='bg-emerald-100'
          iconColor='text-emerald-600'
          subLabel={translate("Paid")}
        />
        <FinancialKpiCard
          label={translate("Remaining")}
          value={formatCurrency(remaining, isHebrew)}
          icon={TrendingDown}
          valueColor={remaining > 0 ? "text-red-600" : "text-gray-500"}
          gradient={remaining > 0 ? "from-red-50 to-white" : "from-gray-50 to-white"}
          iconBg={remaining > 0 ? "bg-red-100" : "bg-gray-100"}
          iconColor={remaining > 0 ? "text-red-500" : "text-gray-400"}
          subLabel={translate("Not yet billed")}
        />
        <FinancialKpiCard
          label={translate("Contractors")}
          value={contractorCount}
          icon={Building2}
          valueColor='text-purple-700'
          gradient='from-purple-50 to-white'
          iconBg='bg-purple-100'
          iconColor='text-purple-600'
        />
      </div>

      {/* Summary mini-cards */}
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
        <SummaryCard
          label={translate("Bills of Quantities")}
          value={boqCount}
          icon={FileText}
          iconBg='bg-blue-100'
          iconColor='text-blue-600'
          valueColor='text-blue-700'
        />
        <SummaryCard
          label={translate("Bills")}
          value={billCount}
          icon={Receipt}
          iconBg='bg-emerald-100'
          iconColor='text-emerald-600'
          valueColor='text-emerald-700'
          badge={billStatusFilter ? translate("filtered") : undefined}
          badgeColor='bg-emerald-50 text-emerald-700'
        />
        <SummaryCard
          label={translate("Budget Utilization")}
          value={`${utilizationPct}%`}
          icon={PercentIcon}
          iconBg={
            utilizationPct >= 90
              ? "bg-red-100"
              : utilizationPct >= 70
                ? "bg-amber-100"
                : "bg-green-100"
          }
          iconColor={
            utilizationPct >= 90
              ? "text-red-500"
              : utilizationPct >= 70
                ? "text-amber-500"
                : "text-green-500"
          }
          valueColor={
            utilizationPct >= 90
              ? "text-red-600"
              : utilizationPct >= 70
                ? "text-amber-600"
                : "text-green-600"
          }
          badge={
            utilizationPct >= 90
              ? translate("High")
              : utilizationPct >= 70
                ? translate("Medium")
                : translate("OK")
          }
          badgeColor={
            utilizationPct >= 90
              ? "bg-red-50 text-red-700"
              : utilizationPct >= 70
                ? "bg-amber-50 text-amber-700"
                : "bg-green-50 text-green-700"
          }
        />
      </div>

      {/* Charts */}
      <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
        {/* BOQ Value by Project */}
        {boqByProject.length > 0 && (
          <ChartCard
            title={translate("BOQ Value by Project")}
            description={translate("Total BOQ value per project")}
          >
            <ChartContainer config={boqProjectConfig} className='h-[290px] w-full'>
              <BarChart data={boqByProject} margin={{ top: 24, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray='3 3' />
                <XAxis
                  dataKey='name'
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  reversed={isHebrew}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(val) => (val.length > 10 ? val.slice(0, 10) + "…" : val)}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  orientation={isHebrew ? "right" : "left"}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(val) =>
                    val >= 1_000_000
                      ? `₪${(val / 1_000_000).toFixed(1)}M`
                      : val >= 1_000
                        ? `₪${(val / 1_000).toFixed(0)}K`
                        : `₪${val}`
                  }
                />
                <ChartTooltip
                  cursor={{ fill: "rgba(0,0,0,0.04)" }}
                  content={
                    <ChartTooltipContent
                      formatter={(value: any) => formatCurrency(Number(value), isHebrew)}
                    />
                  }
                />
                <Bar
                  dataKey='value'
                  radius={[6, 6, 0, 0]}
                  barSize={36}
                  maxBarSize={48}
                  className='cursor-pointer'
                  onClick={(data: any) => {
                    const entry = data?.payload || data
                    if (entry?.projectId) {
                      openDrillDown({
                        type: "boq",
                        projectId: entry.projectId,
                        projectName: entry.name,
                      })
                    }
                  }}
                >
                  {boqByProject.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                  <LabelList content={<BoqBarLabel />} />
                </Bar>
              </BarChart>
            </ChartContainer>
          </ChartCard>
        )}

        {/* Bill Status Donut */}
        {billStatusBreakdown.length > 0 && (
          <ChartCard
            title={translate("Bills by Status")}
            description={
              selectedBillStatus
                ? `${translate("Showing")}: ${billStatusMap.get(selectedBillStatus)?.label || selectedBillStatus}`
                : translate("Click a segment to drill down")
            }
          >
            <ChartContainer
              config={billStatusConfig}
              className='mx-auto h-[290px] w-full'
              dir={isHebrew ? "rtl" : "ltr"}
            >
              <PieChart>
                <Tooltip
                  content={({ active, payload }: any) => {
                    if (!active || !payload?.length) return null
                    const item = payload[0]
                    const count = item.value as number
                    const denominator = billStatusFilter ? billCount : totalBillsUnfiltered
                    const pct = denominator > 0 ? Math.round((count / denominator) * 100) : 0
                    const statusKey = item.payload?.statusKey as string
                    const info = billStatusMap.get(statusKey)
                    return (
                      <div className='rounded-lg border bg-white px-3 py-2 text-sm shadow-lg'>
                        <div className='flex items-center gap-2 font-semibold'>
                          <span
                            className='inline-block h-3 w-3 rounded-sm'
                            style={{ background: item.payload?.fill }}
                          />
                          {info?.label || statusKey}
                        </div>
                        <div className='mt-1 text-muted-foreground'>
                          {count} {translate("bills")} —{" "}
                          <span className='font-bold text-foreground'>{pct}%</span>
                        </div>
                      </div>
                    )
                  }}
                />
                <Pie
                  data={billStatusBreakdown}
                  dataKey='count'
                  nameKey='title'
                  innerRadius='42%'
                  outerRadius='68%'
                  strokeWidth={3}
                  onClick={(data: any) => {
                    const statusKey = data?.statusKey || data?.payload?.statusKey
                    if (statusKey)
                      setSelectedBillStatus(selectedBillStatus === statusKey ? null : statusKey)
                  }}
                  className='cursor-pointer'
                >
                  {billStatusBreakdown.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.fill}
                      opacity={
                        selectedBillStatus && selectedBillStatus !== entry.statusKey ? 0.25 : 1
                      }
                      stroke={selectedBillStatus === entry.statusKey ? "#1f2937" : "white"}
                      strokeWidth={selectedBillStatus === entry.statusKey ? 2.5 : 2}
                    />
                  ))}
                  <Label
                    content={({ viewBox }) => {
                      if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                        return (
                          <text
                            x={viewBox.cx}
                            y={viewBox.cy}
                            textAnchor='middle'
                            dominantBaseline='middle'
                          >
                            <tspan
                              x={viewBox.cx}
                              y={viewBox.cy}
                              className='fill-foreground'
                              fontSize={28}
                              fontWeight={700}
                            >
                              {billStatusFilter ? billCount : totalBillsUnfiltered}
                            </tspan>
                            <tspan
                              x={viewBox.cx}
                              y={(viewBox.cy || 0) + 22}
                              fontSize={11}
                              fill='#6b7280'
                            >
                              {translate("Bills")}
                            </tspan>
                          </text>
                        )
                      }
                    }}
                  />
                </Pie>
                <ChartLegend
                  className='mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1'
                  content={<ChartLegendContent />}
                />
              </PieChart>
            </ChartContainer>
          </ChartCard>
        )}
      </div>

      {/* Drill-Down table for selected bill status */}
      {selectedBillStatus &&
        drillDownBills.length > 0 &&
        (() => {
          const info = billStatusMap.get(selectedBillStatus)
          return (
            <Card className='overflow-hidden border shadow-sm'>
              <CardHeader className='flex flex-row items-center justify-between bg-gray-50/60 pb-3'>
                <div>
                  <CardTitle className='flex items-center gap-2 text-base font-semibold'>
                    <span
                      className='inline-block h-2.5 w-2.5 rounded-full'
                      style={{ background: info?.color || "#6b7280" }}
                    />
                    {translate("Bills")} — {info?.label || selectedBillStatus}
                  </CardTitle>
                  <CardDescription className='mt-0.5 text-xs'>
                    {drillDownBills.length} {translate("bills found")}
                  </CardDescription>
                </div>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => setSelectedBillStatus(null)}
                  className='gap-1.5 text-muted-foreground hover:text-foreground'
                >
                  <X className='h-4 w-4' /> {translate("Close")}
                </Button>
              </CardHeader>
              <CardContent className='px-0 pt-0'>
                <div className='max-h-72 overflow-y-auto'>
                  <table className='w-full border-collapse text-sm'>
                    <thead className='sticky top-0 z-10 bg-gray-50'>
                      <tr className='border-b border-gray-200'>
                        <th
                          className={cn(
                            "px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                            isHebrew ? "text-right" : "text-left",
                          )}
                        >
                          {translate("Bill #")}
                        </th>
                        <th
                          className={cn(
                            "px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                            isHebrew ? "text-right" : "text-left",
                          )}
                        >
                          {translate("Date")}
                        </th>
                        <th
                          className={cn(
                            "px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                            isHebrew ? "text-right" : "text-left",
                          )}
                        >
                          {translate("BOQ")}
                        </th>
                        <th
                          className={cn(
                            "px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                            isHebrew ? "text-right" : "text-left",
                          )}
                        >
                          {translate("Amount")}
                        </th>
                        <th
                          className={cn(
                            "px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                            isHebrew ? "text-right" : "text-left",
                          )}
                        >
                          {translate("Status")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-100'>
                      {drillDownBills.slice(0, 50).map((bill: any, idx: number) => {
                        const amount = computeBillTotal(bill, false)
                        const billDate = bill.date || bill.created_at
                        const dateStr = billDate
                          ? new Date(billDate).toLocaleDateString(isHebrew ? "he-IL" : "en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "—"
                        return (
                          <tr
                            key={bill.id || idx}
                            className='transition-colors hover:bg-gray-50/80'
                          >
                            <td className='px-4 py-3 font-medium text-foreground'>
                              {bill.bill_number || bill.id || idx + 1}
                            </td>
                            <td className='px-4 py-3 text-sm tabular-nums text-muted-foreground'>
                              {dateStr}
                            </td>
                            <td className='max-w-[140px] truncate px-4 py-3 text-sm text-foreground'>
                              {boqNameMap[bill._boqId] || "—"}
                            </td>
                            <td className='px-4 py-3 font-mono font-semibold text-foreground'>
                              {formatCurrency(amount, isHebrew)}
                            </td>
                            <td className='px-4 py-3'>
                              <span
                                className='inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold'
                                style={{
                                  background: `${info?.color || "#6b7280"}1a`,
                                  color: info?.color || "#6b7280",
                                }}
                              >
                                <span
                                  className='h-1.5 w-1.5 rounded-full'
                                  style={{
                                    background: info?.color || "#6b7280",
                                  }}
                                />
                                {info?.label || selectedBillStatus}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    {drillDownBills.length > 50 && (
                      <tfoot>
                        <tr>
                          <td
                            colSpan={5}
                            className='bg-gray-50 px-4 py-2 text-center text-xs text-muted-foreground'
                          >
                            {translate("Showing")} 50 / {drillDownBills.length} {translate("bills")}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </CardContent>
            </Card>
          )
        })()}

      {/* Budget Utilization Bar */}
      {totalBoqValue > 0 && (
        <Card
          className='cursor-pointer overflow-hidden shadow-sm transition-shadow hover:shadow-md'
          onClick={() => openDrillDown({ type: "bill-status", statusKey: "__utilization__" })}
        >
          <CardContent className='p-5'>
            <div className='flex flex-wrap items-center gap-4'>
              <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50'>
                <Banknote className='h-5 w-5 text-blue-600' />
              </div>
              <div className='min-w-0'>
                <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  {translate("Budget Utilization")}
                </p>
                <p className='mt-0.5 text-sm font-medium text-foreground'>
                  {formatCurrency(totalBilled, isHebrew)}{" "}
                  <span className='text-muted-foreground'>/</span>{" "}
                  {formatCurrency(totalBoqValue, isHebrew)}
                </p>
              </div>
              <div className='relative mx-2 h-4 min-w-[120px] flex-1 overflow-hidden rounded-full bg-gray-100'>
                <div
                  className='h-full rounded-full transition-all duration-700 ease-out'
                  style={{
                    width: `${Math.min(utilizationPct, 100)}%`,
                    background:
                      utilizationPct >= 90
                        ? "linear-gradient(to right, #22c55e, #f59e0b, #ef4444)"
                        : utilizationPct >= 70
                          ? "linear-gradient(to right, #22c55e, #f59e0b)"
                          : "linear-gradient(to right, #3b82f6, #22c55e)",
                  }}
                />
              </div>
              <span className='text-2xl font-extrabold tabular-nums' style={{ color: utilColor }}>
                {utilizationPct}%
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Drill-Down Sheet */}
      <DrillDownSheet
        open={!!drillDown}
        onClose={closeDrillDown}
        title={getDrillDownTitle(drillDown, translate)}
        subtitle={getDrillDownSubtitle(drillDown, translate)}
        width='wide'
      >
        {drillDown?.type === "boq" && (
          <DrillDownTable
            columns={[
              { header: translate("BOQ Name"), accessor: "name", sortable: true },
              {
                header: translate("Total Value"),
                accessor: "total",
                sortable: true,
                render: (v: number) => formatCurrency(v, isHebrew),
              },
              {
                header: translate("Billed"),
                accessor: "billed",
                sortable: true,
                render: (v: number) => formatCurrency(v, isHebrew),
              },
              {
                header: translate("Remaining"),
                accessor: "remaining",
                sortable: true,
                render: (v: number) => formatCurrency(v, isHebrew),
              },
              {
                header: translate("Utilization") + " %",
                accessor: "utilization",
                sortable: true,
                render: (v: number) => (
                  <span className='font-semibold' style={{ color: utilizationColor(v) }}>
                    {v}%
                  </span>
                ),
              },
            ]}
            data={drillDownBoqsByProject}
          />
        )}
        {drillDown?.type === "bill-status" && drillDown.statusKey === "__utilization__" && (
          <DrillDownTable
            columns={[
              { header: translate("BOQ Name"), accessor: "name", sortable: true },
              { header: translate("Project"), accessor: "project", sortable: true },
              {
                header: translate("Total Value"),
                accessor: "total",
                sortable: true,
                render: (v: number) => formatCurrency(v, isHebrew),
              },
              {
                header: translate("Billed"),
                accessor: "billed",
                sortable: true,
                render: (v: number) => formatCurrency(v, isHebrew),
              },
              {
                header: translate("Remaining"),
                accessor: "remaining",
                sortable: true,
                render: (v: number) => formatCurrency(v, isHebrew),
              },
              {
                header: translate("Utilization") + " %",
                accessor: "utilization",
                sortable: true,
                render: (v: number) => (
                  <span className='font-semibold' style={{ color: utilizationColor(v) }}>
                    {v}%
                  </span>
                ),
              },
            ]}
            data={drillDownAllBoqsUtilization}
          />
        )}
      </DrillDownSheet>
    </div>
  )
}

export default FinancialAnalytics
